import { useState, useCallback } from "react";
import { InvoiceLineInput } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ItemSelector } from "@/components/ItemSelector";
import { useItemsStore } from "@/modules/items/itemsStore";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import Decimal from "decimal.js";

interface InvoiceLineItemsEditorProps {
  lines: InvoiceLineInput[];
  onChange: (lines: InvoiceLineInput[]) => void;
  disabled?: boolean;
}

export function InvoiceLineItemsEditor({
  lines,
  onChange,
  disabled = false,
}: InvoiceLineItemsEditorProps) {
  const { items } = useItemsStore();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const handleAddLine = useCallback(() => {
    if (disabled) {
      return;
    }
    const newLine: InvoiceLineInput = {
      itemId: null,
      description: "",
      quantity: "1",
      unitPrice: "0.00",
    };
    onChange([...lines, newLine]);
    setFocusedIndex(lines.length);
  }, [lines, onChange, disabled]);

  const handleRemoveLine = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }
      const newLines = lines.filter((_, i) => i !== index);
      onChange(newLines);
      setFocusedIndex(null);
    },
    [lines, onChange, disabled],
  );

  const handleLineChange = useCallback(
    (index: number, field: keyof InvoiceLineInput, value: string | null) => {
      if (disabled) {
        return;
      }
      const newLines = [...lines];
      newLines[index] = {
        ...newLines[index],
        [field]: value,
      };
      onChange(newLines);
    },
    [lines, onChange, disabled],
  );

  const handleItemSelect = useCallback(
    (index: number, itemId: string | null) => {
      if (disabled) {
        return;
      }
      if (!itemId) {
        handleLineChange(index, "itemId", null);
        handleLineChange(index, "description", "");
        handleLineChange(index, "unitPrice", "0.00");
        return;
      }

      const item = items.get(itemId);
      if (item) {
        const newLines = [...lines];
        newLines[index] = {
          ...newLines[index],
          itemId: item.id,
          description: item.description || item.name,
          quantity: "1",
        };
        onChange(newLines);
      }
    },
    [items, lines, onChange, handleLineChange, disabled],
  );

  const calculateLineAmount = (line: InvoiceLineInput): string => {
    try {
      const quantity = new Decimal(line.quantity || 0);
      const unitPrice = new Decimal(line.unitPrice || 0);
      return quantity.mul(unitPrice).toFixed(2);
    } catch {
      return "0.00";
    }
  };

  const calculateTotal = (): string => {
    const total = lines.reduce((sum, line) => {
      try {
        const quantity = new Decimal(line.quantity || 0);
        const unitPrice = new Decimal(line.unitPrice || 0);
        return sum.add(quantity.mul(unitPrice));
      } catch {
        return sum;
      }
    }, new Decimal(0));
    return total.toFixed(2);
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr className="border-b">
              <th className="text-left p-2 text-sm font-medium w-[30%]">
                Item
              </th>
              <th className="text-left p-2 text-sm font-medium w-[25%]">
                Description
              </th>
              <th className="text-right p-2 text-sm font-medium w-[10%]">
                Qty
              </th>
              <th className="text-right p-2 text-sm font-medium w-[15%]">
                Unit Price
              </th>
              <th className="text-right p-2 text-sm font-medium w-[15%]">
                Amount
              </th>
              <th className="p-2 w-[5%]"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr
                key={index}
                className={cn(
                  "border-b hover:bg-muted/30 transition-colors",
                  focusedIndex === index && "bg-muted/50",
                )}
                onClick={() => setFocusedIndex(index)}
              >
                <td className="p-2">
                  <ItemSelector
                    currentlySelectedId={line.itemId || null}
                    onSelectId={(id) => handleItemSelect(index, id)}
                    placeholder="Select item..."
                    className="h-8"
                    disabled={disabled}
                    enableQuickCreate
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={line.description || ""}
                    onChange={(e) =>
                      handleLineChange(index, "description", e.target.value)
                    }
                    placeholder="Enter description..."
                    className="h-8 border-0 bg-transparent"
                    disabled={disabled}
                  />
                </td>
                <td className="p-2">
                  <Input
                    type="number"
                    value={line.quantity}
                    onChange={(e) =>
                      handleLineChange(index, "quantity", e.target.value)
                    }
                    placeholder="1"
                    className="h-8 border-0 bg-transparent text-right"
                    min="0"
                    step="1"
                    disabled={disabled}
                  />
                </td>
                <td className="p-2">
                  <div className="flex items-center justify-end">
                    <span className="text-sm text-muted-foreground mr-1">
                      $
                    </span>
                    <Input
                      type="number"
                      value={line.unitPrice}
                      onChange={(e) =>
                        handleLineChange(index, "unitPrice", e.target.value)
                      }
                      placeholder="0.00"
                      className="h-8 border-0 bg-transparent text-right"
                      min="0"
                      step="0.01"
                      disabled={disabled}
                    />
                  </div>
                </td>
                <td className="p-2 text-right">
                  <span className="text-sm font-medium">
                    ${calculateLineAmount(line)}
                  </span>
                </td>
                <td className="p-2">
                  {!disabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLine(index);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="p-8 text-center text-muted-foreground"
                >
                  No line items added. Click "Add Line" to get started.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="p-2 text-right font-medium">
                Total
              </td>
              <td className="p-2 text-right">
                <span className="text-lg font-bold">${calculateTotal()}</span>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!disabled && (
        <Button
          onClick={handleAddLine}
          variant="outline"
          className="w-full"
          type="button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Line
        </Button>
      )}
    </div>
  );
}
